import { type PiniaCustomProperties } from 'pinia'

import { StoreSendRequestError } from '@services/errorHandling'
import { createRateLimitedRequest, type RequestOptions, watchRequest } from '@services/api/utils'

import {
  type Mailbox,
  type GetMailboxRequest,
  type GetMailboxesRequest,
  type DeleteMailboxRequest,
  type GetMailboxProfilesRequest,
  type PostMailboxRequest,
  type PatchMailboxRequest,
  type PostMailboxProfilesRequest,
  type PostDuplicateMailboxRequest,
  type DeleteMailboxProfileRequest,
} from '@services/Communications/declarations'

import {
  deleteMailboxConfig,
  deleteMailboxProfileConfig,
  getMailboxConfig,
  getMailboxesConfig,
  getMailboxProfilesConfig,
  patchMailboxConfig,
  postDuplicateMailboxConfig,
  postMailboxConfig,
  postMailboxProfilesConfig,
} from '@services/Communications/api'

import { type WatchRequestConfig } from '@services/api/declarations'

// ***
// COMMON
// ***

const serviceName = 'Communications'

// ***
// ERROR INFORMATION
// ***

const missingClubHashError = (caller: string) => new StoreSendRequestError({
  service: serviceName,
  message: 'clubHash param is required but not found',
  caller,
})

const missingMailboxHashError = (caller: string) => new StoreSendRequestError({
  service: serviceName,
  message: 'mailboxHash param is required but not found',
  caller,
})

const missingProfileHashError = (caller: string) => new StoreSendRequestError({
  service: serviceName,
  message: 'profileHash param is required but not found',
  caller,
})

// ***
// REQUESTS
// ***
export function useMailboxesModule<S extends PiniaCustomProperties>(store: S) {
  const { sendRequest } = store

  // FETCH GET MAILBOXES
  function fetchGetMailboxes(
    requestConfig: GetMailboxesRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetMailboxes')

    createRateLimitedRequest<GetMailboxesRequest>({
      sendRequest,
      endpoint: getMailboxesConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetMailboxes(
    { onSuccess, onError }: WatchRequestConfig<Mailbox[]>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox[]>(getMailboxesConfig, {
      onSuccess: (data, totalItems) => onSuccess?.(data, totalItems),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // FETCH GET MAILBOX
  function fetchGetMailbox(
    requestConfig: GetMailboxRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetMailbox')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('fetchGetMailbox')

    createRateLimitedRequest<GetMailboxRequest>({
      sendRequest,
      endpoint: getMailboxConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetMailbox(
    { onSuccess, onError }: WatchRequestConfig<Mailbox>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox>(getMailboxConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST MAILBOX
  function fetchPostMailbox(
    requestConfig: PostMailboxRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchPostMailbox')

    createRateLimitedRequest<PostMailboxRequest>({
      sendRequest,
      endpoint: postMailboxConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostMailbox(
    { onSuccess, onError }: WatchRequestConfig<Mailbox>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox>(postMailboxConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // PATCH MAILBOX
  function fetchPatchMailbox(
    requestConfig: PatchMailboxRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchPatchMailbox')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('fetchPatchMailbox')

    createRateLimitedRequest<PatchMailboxRequest>({
      sendRequest,
      endpoint: patchMailboxConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPatchMailbox(
    { onSuccess, onError }: WatchRequestConfig<Mailbox>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox>(patchMailboxConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // DELETE MAILBOX
  function fetchDeleteMailbox(
    requestConfig: DeleteMailboxRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('deleteMailbox')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('deleteMailbox')

    createRateLimitedRequest<DeleteMailboxRequest>({
      sendRequest,
      endpoint: deleteMailboxConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchDeleteMailbox(
    { onSuccess, onError }: WatchRequestConfig<Mailbox>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox>(deleteMailboxConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST DUPLICATE MAILBOX
  function fetchPostDuplicateMailbox(
    requestConfig: PostDuplicateMailboxRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('deleteMailbox')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('deleteMailbox')

    createRateLimitedRequest<PostDuplicateMailboxRequest>({
      sendRequest,
      endpoint: postDuplicateMailboxConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostDuplicateMailbox(
    { onSuccess, onError }: WatchRequestConfig<Mailbox>,
  ) {
    const { closeSubscription } = watchRequest<Mailbox>(postDuplicateMailboxConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // GET MAILBOX PROFILES
  function fetchGetMailboxProfiles(
    requestConfig: GetMailboxProfilesRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchGetMailboxProfiles')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('fetchGetMailboxProfiles')

    createRateLimitedRequest<GetMailboxProfilesRequest>({
      sendRequest,
      endpoint: getMailboxProfilesConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchGetMailboxProfiles(
    { onSuccess, onError }: WatchRequestConfig<unknown>,
  ) {
    const { closeSubscription } = watchRequest<unknown>(getMailboxProfilesConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST MAILBOX PROFILES
  function fetchPostMailboxProfiles(
    requestConfig: PostMailboxProfilesRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchPostMailboxProfiles')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('fetchPostMailboxProfiles')

    createRateLimitedRequest<PostMailboxProfilesRequest>({
      sendRequest,
      endpoint: postMailboxProfilesConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostMailboxProfiles(
    { onSuccess, onError }: WatchRequestConfig<unknown>,
  ) {
    const { closeSubscription } = watchRequest<unknown>(postMailboxProfilesConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // DELETE MAILBOX PROFILE
  function fetchDeleteMailboxProfile(
    requestConfig: DeleteMailboxProfileRequest,
    options?: RequestOptions,
  ) {
    if (!requestConfig?.params?.clubHash) throw missingClubHashError('fetchDeleteMailboxProfile')
    if (!requestConfig?.params?.mailboxHash) throw missingMailboxHashError('fetchDeleteMailboxProfile')
    if (!requestConfig?.params?.profileHash) throw missingProfileHashError('fetchDeleteMailboxProfile')

    createRateLimitedRequest<DeleteMailboxProfileRequest>({
      sendRequest,
      endpoint: deleteMailboxProfileConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchDeleteMailboxProfile(
    { onSuccess, onError }: WatchRequestConfig<unknown>,
  ) {
    const { closeSubscription } = watchRequest<unknown>(deleteMailboxProfileConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  return {
    fetchGetMailbox,
    fetchPostMailbox,
    fetchPatchMailbox,
    fetchGetMailboxes,
    fetchDeleteMailbox,
    fetchPostDuplicateMailbox,
    fetchGetMailboxProfiles,
    fetchPostMailboxProfiles,
    fetchDeleteMailboxProfile,

    // watchers
    watchDeleteMailboxProfile,
    watchPostDuplicateMailbox,
    watchDeleteMailbox,
    watchGetMailbox,
    watchPostMailbox,
    watchPatchMailbox,
    watchGetMailboxes,
    watchGetMailboxProfiles,
    watchPostMailboxProfiles,
  }
}
